// Transaction Wizard — the runner's brain: loads the models, keeps the run
// (memory store, and the server store for `suspend: "server"`), drives the
// machine through the non-interactive steps, and exposes what the shell and the
// step components need (docs/plans/transaction-wizard.md §8–§10).
//
// The run is the single source of truth (`runRef`); every async step reads the
// latest run from it and commits a new one, so a slow item_change never lands
// on a frame the user has already left.

import { createElement, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { trackActivity } from '../activity'
import { useWarningConfirm } from '../txn/WarningsConfirmModal'
import { useReasonPrompt } from '../txn/ReasonForChangeModal'
import { useActionConfirm } from '../txn/ActionConfirmModal'
import HtmlResultModal from '../txn/HtmlResultModal'
import type { TxnScreenRuntime } from '../txn/screen/contract'
import type { OpenActionBinding, TransactionModel } from '../txn/types'
import { buildOpenActionRequest } from '../txn/types'
import { isKey } from '../txn/utils'
import type {
  WizardCallBlock, WizardCallOutcome, WizardConfirmResponse, WizardContextPanel, WizardFrame, WizardHost,
  WizardInput, WizardModel, WizardRedirectStartResponse, WizardRun, WizardRunnerProps,
  WizardStep, WizardTool,
} from './contract'
import {
  buildFormIndex, evalCondition, frameValues, isBlank, isInteractive, keyOf, nextRoute, normalizeWizard,
  parseTarget, readCol, render, renderMap, routeOf, scopeOf, stepById,
  text, unpad, type FormIndex, type Route, type Row, type WizardScope,
} from './model'
import {
  START_STEP, WizardError, back as machineBack, boundary, callBlockOf, canBack, checkCall,
  computeOutputs, enterStep, fillResult, finishOutcome, gridRequired, issueRoute, mapIssues, missingInputs,
  assigneeWaitingOn, handbackOutcome, isFinalOutcome, markLeft, missingRequired, newFrame,
  newRunId, popCall, readyToReturn, waitingOnFor, pushCall, remainingEstimate, repeatProgress,
  repeatStep, resumeTarget, returnRoute, returnSets, routeAfter, trail, visit,
  type StepIssue,
} from './machine'
import {
  callFunction, pollStep, redirectStart, redirectStatus,
  completeLine, confirmPassword as confirmPasswordOp, documentOp, fetchUrl, fireItemChange, hasBlocking, initAdd, issuesFromError,
  itemChangeInvokable, loadRecord, locate, runAction, runOps, saveRecord, scanParse, setValue,
  splitSets, stagesLine, validateTree, writeValue, addLine, deleteLine,
  type ChooseRequest, type OpsEnv, type WizardIssue,
} from './ops'
import { lineEntryApi, toBufferRow } from '../txn/lineEntry/contract'
import {
  clearMemoryRun, memoryRunFor, readMemoryRun, serverRunStore, shouldPersist, slotKey,
  writeMemoryRun,
} from './runStore'
import { beep, buzz } from './feedback'
import { createSerialQueue, type SerialQueue } from './scanQueue'
// Offline (Phase 4): a client-side scan parse and device-kept runs, used only
// for an `offline: true` wizard / while the host reports it is offline.
import { isHostOffline, parseScanInScope } from './scanParse'
import { hydrateDeviceRuns, syncDeviceRun } from './deviceRunStore'
import ChooseDialog from './steps/ChooseDialog'
import ConfirmDialog from './steps/ConfirmDialog'

/** Wizard activity events carry their object type, so reports can group
 *  them with every other object's. */
function trackWizard(event: string, wizard: string, fields: Record<string, unknown>): void {
  trackActivity(event, wizard, { obj_type: 'U', ...fields })
}

interface Loaded { model: WizardModel; base: TransactionModel; fi: FormIndex }

export interface ScanLogEntry { id: number; at: number; text: string; ok: boolean; message: string }

/** Per-entry UI state of the step on screen (reset whenever a step is entered). */
export interface StepUi {
  key: string
  ack: boolean
  confirmed: boolean
  printed: boolean
  /** A `sign` step: every signature this person can give is given (or the
   *  document is fully signed). */
  signed: boolean
  /** A `send` step: the send was queued. */
  sent: boolean
  scanned: boolean
  scanLog: ScanLogEntry[]
  /** Grid rows edited since the step was entered (completed on Next). */
  edited: number[]
  fieldErrors: Record<string, string>
  gridErrors: Record<number, Record<string, string>>
}

export type WizardPhase = 'loading' | 'start' | 'ready' | 'paused' | 'finished' | 'error'

/** One cell a grid paste / fill-down sets: a line index, or the n-th row the
 *  same call adds first (`{add: n}`), and the value as typing would give it. */
export interface GridCellSet {
  line: number | { add: number }
  column: string
  value: unknown
}

export interface WizardController {
  phase: WizardPhase
  /** Running a `run_as` child for its role: the top frame only, handed back
   *  to the requester when done. */
  assignee: boolean
  /** Assignee: hand the child back as failed (it cannot be completed). */
  handBackFailed(): Promise<void>
  error: string | null
  host: WizardHost
  run: WizardRun | null
  frame: WizardFrame | null
  frameIndex: number
  model: WizardModel | null
  base: TransactionModel | null
  fi: FormIndex | null
  step: WizardStep | null
  scope: WizardScope | null
  screen: TxnScreenRuntime | null
  busy: string | null
  issues: StepIssue[]
  /** Required inputs nobody supplied (the Start step). */
  startInputs: WizardInput[]
  backState: { ok: boolean; reason?: string }
  breadcrumb: Array<{ wizard: string; title: string }>
  paused: string | null
  finished: { message: string; outcome: WizardCallOutcome } | null
  refreshKey: number
  ui: StepUi
  repeat: { index: number; total: number } | null
  remaining: number
  /** The interactive steps the frame went through, in order (the rail). */
  path: string[]
  modals: ReactNode
  // actions
  next(): Promise<void>
  back(): void
  cancel(): Promise<void>
  pause(): Promise<void>
  /** Continue a run paused in this tab. */
  resume(): void
  restart(): Promise<void>
  jumpTo(stepId: string, line?: { form: string; index: number | null } | null): Promise<void>
  change(target: string, value: unknown, lineIndex?: number | null): void
  setUi(patch: Partial<StepUi>): void
  submitStart(values: Record<string, unknown>): Promise<void>
  select(rows: Row[]): Promise<void>
  scan(text: string): Promise<boolean>
  /** A scan queued behind the ones still being processed (a wedge scanner
   *  firing faster than the server answers): processed in order, never
   *  dropped. Resolves with whether it was accepted. */
  enqueueScan(text: string): Promise<boolean>
  /** The latest entry of the scan log (what the last scan said). */
  lastScan(): ScanLogEntry | null
  undoScan(): void
  /** `confirm.password`: check the caller's own password on the server;
   *  verified ⇒ the step is confirmed. */
  confirmPassword(password: string): Promise<WizardConfirmResponse>
  /** Grid paste / fill-down: add `addRows` lines (`allow_add`), then set
   *  each cell in order through the typing path (item_change per cell). */
  setCells(cells: GridCellSet[], addRows?: number): Promise<void>
  answerCall(yes: boolean): Promise<void>
  print(): Promise<void>
  /** A `send` step: queue the base transaction's send for the saved record. */
  send(): Promise<void>
  runTool(tool: WizardTool, lineIndex?: number | null): Promise<void>
  toolVisible(tool: WizardTool): boolean
  pickRow(panel: WizardContextPanel, row: Row): Promise<void>
  addGridLine(): Promise<void>
  deleteGridLine(index: number): void
  retry(): Promise<void>
  /** A `redirect` step: mint its callback and get the other site's URL. */
  redirectOpen(): Promise<WizardRedirectStartResponse>
  /** A `redirect` step: has the other site called back? `done` — its answer
   *  is in the record and the wizard moved on; `failed` — it was refused
   *  (the issues are shown). */
  redirectPoll(): Promise<'none' | 'waiting' | 'expired' | 'done' | 'failed'>
  /** A `function` step's watched job: `server` false — the browser saw it
   *  done, finish it on the server; true — the browser cannot read the
   *  status address, let the server look (at most once per interval). */
  pollJob(server: boolean): Promise<'waiting' | 'done' | 'failed' | 'expired'>
  /** The record's lines on a form (live rows with their index). */
  linesOf(form: string): Array<{ index: number; row: Row }>
}

const EMPTY_UI = (key: string): StepUi => ({
  key, ack: false, confirmed: false, printed: false, signed: false, sent: false, scanned: false, scanLog: [],
  edited: [], fieldErrors: {}, gridErrors: {},
})

const isFinal = isFinalOutcome

export function useWizard(props: WizardRunnerProps): WizardController {
  const { host, wizard, inputs, resumeRunId } = props
  const [phase, setPhase] = useState<WizardPhase>('loading')
  const [error, setError] = useState<string | null>(null)
  const [run, setRunState] = useState<WizardRun | null>(null)
  const runRef = useRef<WizardRun | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const busyRef = useRef(false)
  const [issues, setIssues] = useState<StepIssue[]>([])
  const [startInputs, setStartInputs] = useState<WizardInput[]>([])
  const [paused, setPaused] = useState<string | null>(null)
  const [finished, setFinished] = useState<{ message: string; outcome: WizardCallOutcome } | null>(null)
  const [refreshKey, setRefreshKey] = useState(0)
  const [ui, setUiState] = useState<StepUi>(EMPTY_UI(''))
  const [htmlModal, setHtmlModal] = useState<{ title: string; html: string } | null>(null)
  const [chooser, setChooser] = useState<(ChooseRequest & { resolve: (rows: Row[] | null) => void }) | null>(null)
  const [confirmer, setConfirmer] = useState<{ message: string; yes: string; no: string; resolve: (ok: boolean) => void } | null>(null)
  const [toolAllowed, setToolAllowed] = useState<Record<string, boolean>>({})
  const loaded = useRef(new Map<string, Loaded>())
  const screens = useRef(new Map<string, TxnScreenRuntime>())
  const persisted = useRef(false)
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>())
  const pendingChanges = useRef(new Map<string, () => Promise<void>>())
  const scanUndo = useRef<WizardFrame[]>([])
  const lastScanRef = useRef<ScanLogEntry | null>(null)
  // Running a `run_as` child for its role: only the top frame is theirs, and
  // it is handed back, never popped into the requester's frame.
  const assigneeRef = useRef(false)
  const handedBack = useRef(false)
  const slot = useMemo(() => slotKey(host.project, wizard, inputs), [host.project, wizard, inputs])

  const { confirmWarnings, warningsModal } = useWarningConfirm()
  const { promptReason, reasonModal } = useReasonPrompt()
  const { confirmAction, actionConfirmModal } = useActionConfirm()

  const notify = host.notify
  const choose = useCallback((req: ChooseRequest) => new Promise<Row[] | null>((resolve) => {
    setChooser({ ...req, resolve })
  }), [])
  const ask = useCallback((message: string, yes = 'Yes', no = 'No') => new Promise<boolean>((resolve) => {
    setConfirmer({ message, yes, no, resolve })
  }), [])

  // ── Models and transports ──────────────────────────────────────────────

  const ensureLoaded = useCallback(async (name: string): Promise<Loaded> => {
    const hit = loaded.current.get(name)
    if (hit) return hit
    const model = normalizeWizard(await host.loadWizard(name))
    const base = await host.loadTxnModel(model.base_object)
    const entry = { model, base, fi: buildFormIndex(base, model.line_form) }
    loaded.current.set(name, entry)
    return entry
  }, [host])

  const screenFor = useCallback((name: string, runId: string): TxnScreenRuntime => {
    const key = `${name} ${runId}`
    let s = screens.current.get(key)
    if (!s) {
      s = host.screenRuntime({ obj_name: name, run_id: runId })
      screens.current.set(key, s)
    }
    return s
  }, [host])

  const L = (name: string): Loaded => {
    const l = loaded.current.get(name)
    if (!l) throw new WizardError('WIZARD_NOT_LOADED', `Wizard "${name}" is not loaded.`)
    return l
  }

  const envFor = useCallback((r: WizardRun, idx: number, stepId?: string, extra?: Partial<WizardScope>): OpsEnv => {
    const f = r.stack[idx]
    const l = L(f.wizard)
    return {
      rt: screenFor(f.wizard, r.run_id),
      project: host.project,
      model: l.model, base: l.base, fi: l.fi,
      via: { obj_name: f.wizard, run_id: r.run_id },
      stepId: stepId ?? f.cursor,
      frameIndex: idx,
      user: host.user,
      notify,
      host,
      confirmWarnings, confirmAction,
      promptReason: (m) => promptReason(m),
      choose,
      showHtml: (title, html) => setHtmlModal({ title, html }),
      extra,
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [host, screenFor, notify, confirmWarnings, confirmAction, promptReason, choose])

  // ── Committing the run ─────────────────────────────────────────────────

  /** What the Work List calls a kept run: the wizard's title and the record
   *  it is working on ("Dispense material — WO-2601"), not its object name. */
  const runTitle = (r: WizardRun): string => {
    const f0 = r.stack[0]
    const l = f0 ? loaded.current.get(f0.wizard) : undefined
    const name = String(l?.model.title || r.wizard)
    if (!l || !f0) return name
    const keys = (l.fi.forms[l.fi.headerNo]?.cols || []).filter(isKey)
    const vals = keys
      .map(c => unpad(readCol(f0.record.header, l.fi, l.fi.headerNo, String(c.name || c.db_name || ''))))
      .filter(v => !isBlank(v))
      .map(String)
    return (vals.length ? `${name} — ${vals.join(' / ')}` : name).slice(0, 200)
  }

  const persist = useCallback((r: WizardRun, reason: 'call' | 'suspend' | 'step') => {
    if (handedBack.current) return
    const root = loaded.current.get(r.wizard)?.model
    if (!shouldPersist(root?.suspend, reason, persisted.current, assigneeRef.current)) return
    persisted.current = true
    // An assignee saves progress through the child wizard (their grant); the
    // server keeps the run's owner, status and waiting_on.
    const via = assigneeRef.current ? r.stack[r.stack.length - 1].wizard : r.wizard
    serverRunStore(screenFor(via, r.run_id), via).save({ ...r, title: r.title || runTitle(r), updated_at: new Date().toISOString() })
      .catch((err) => notify('warning', `The run could not be saved for later: ${err instanceof Error ? err.message : String(err)}`))
  }, [screenFor, notify])

  const commit = useCallback((r: WizardRun, reason?: 'call' | 'suspend' | 'step') => {
    const prevTop = runRef.current?.stack[runRef.current.stack.length - 1]
    runRef.current = r
    setRunState(r)
    // An offline wizard's run is mirrored to device storage (finished ⇒ dropped).
    if (loaded.current.get(r.wizard)?.model.offline || isHostOffline(host)) syncDeviceRun(host.project, r, slot)
    // A finished run is forgotten (finish / cancel cleared it), not re-stored.
    if (r.status === 'completed' || r.status === 'cancelled') return
    writeMemoryRun(r, slot)
    const top = r.stack[r.stack.length - 1]
    if (reason) persist(r, reason)
    else if (prevTop && top && (prevTop.cursor !== top.cursor || prevTop.wizard !== top.wizard)) persist(r, 'step')
  }, [slot, persist, host])

  const replaceTop = (r: WizardRun, f: WizardFrame): WizardRun => {
    const stack = r.stack.slice()
    stack[stack.length - 1] = f
    return { ...r, stack }
  }

  const top = (r: WizardRun) => r.stack[r.stack.length - 1]

  const scopeFrom = useCallback((r: WizardRun, f: WizardFrame, step?: WizardStep | null, extra?: Partial<WizardScope>) => {
    const l = L(f.wizard)
    return scopeOf(f, l.fi, { step, run: { run_id: r.run_id }, user: host.user, extra })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [host.user])

  const withBusy = useCallback(async <T, >(label: string, fn: () => Promise<T>): Promise<T | undefined> => {
    if (busyRef.current) return undefined
    busyRef.current = true
    setBusy(label)
    try {
      return await fn()
    } finally {
      busyRef.current = false
      setBusy(null)
    }
  }, [])

  // ── Issues and routing by code ─────────────────────────────────────────

  const showIssues = useCallback((r: WizardRun, list: WizardIssue[]) => {
    const f = top(r)
    const l = loaded.current.get(f.wizard)
    if (!l) return
    setIssues(mapIssues(l.model, l.fi, list))
  }, [])

  /** `on_issue`: the first issue whose code the step routes. */
  const issueRouting = (step: WizardStep | null, list: WizardIssue[]) => {
    for (const i of list) {
      const hit = issueRoute(step, i.code)
      if (hit) return hit
    }
    return null
  }

  // ── The driver ─────────────────────────────────────────────────────────
  // Declared as refs-of-functions so drive / finish / startCall can call each
  // other without a dependency cycle between useCallbacks.

  const fns = useRef<{
    drive: (r: WizardRun, route: Route | null) => Promise<WizardRun>
    finish: (r: WizardRun) => Promise<WizardRun>
    startCall: (r: WizardRun, block: WizardCallBlock, callingStep: string, viaTool: boolean) => Promise<WizardRun>
    handleReturn: (r: WizardRun) => Promise<WizardRun>
    initFrame: (r: WizardRun) => Promise<WizardRun>
  }>(null as never)

  /** Stop on `stepId` showing `list` (an auto step that failed, a refused save). */
  const stopOn = (r: WizardRun, stepId: string, list: WizardIssue[]): WizardRun => {
    const f = top(r)
    const at = f.cursor === stepId ? f : enterStep(f, stepId)
    const next = replaceTop(r, trail(at, 'issue', stepId, list[0]?.message))
    showIssues(next, list)
    return next
  }

  const drive = async (start: WizardRun, firstRoute: Route | null): Promise<WizardRun> => {
    let r = start
    let route = firstRoute
    for (let guard = 0; guard < 2000; guard++) {
      const idx = r.stack.length - 1
      let f = top(r)
      const { model, fi } = L(f.wizard)
      if (!route || 'finish' in route) return fns.current.finish(r)
      const step = stepById(model, route.goto)
      if (!step) {
        notify('error', `The wizard has no step "${route.goto}".`)
        return r
      }
      try {
        f = visit(f, model, step.id)
      } catch (err) {
        return stopOn(r, step.id, [{ message: (err as Error).message, code: (err as WizardError).code || null, type: 'E' }])
      }
      r = replaceTop(r, f)
      let scope = scopeFrom(r, f, step)
      if (step.when && !evalCondition(step.when, scope, true)) {
        route = routeAfter(model, f, step, scope)
        continue
      }
      if (isInteractive(step.kind)) {
        let entered = trail(enterStep(f, step.id), 'step', step.id)
        // An input field's `default` (a template) pre-fills a BLANK target as
        // if typed, so its item_change runs; a value already there — captured
        // earlier, or typed before going Back — is never overwritten.
        if (step.kind === 'input') {
          for (const fld of step.fields || []) {
            if (typeof fld === 'string' || fld.default === undefined || fld.default === null) continue
            const here = scopeFrom(replaceTop(r, entered), entered, step)
            if (!isBlank(render(`{${fld.field}}`, here))) continue
            const value = render(fld.default, here)
            if (isBlank(value)) continue
            const res = await setValue(envFor(replaceTop(r, entered), idx, step.id), entered, fld.field, value)
            if (hasBlocking(res.issues)) {
              if (typeof console !== 'undefined') console.warn('[wizard] field default not applied', fld.field, res.issues)
              continue
            }
            entered = res.frame
          }
        }
        r = replaceTop(r, entered)
        trackWizard('wizard_step', f.wizard, { step: step.id, kind: step.kind })
        setIssues([])
        // A confirm step's dry run is part of what it shows.
        if (step.kind === 'confirm' && step.validate) {
          const list = await validateTree(envFor(r, idx, step.id), entered)
          showIssues(r, list)
        }
        return r
      }
      const env = envFor(r, idx, step.id)
      switch (step.kind) {
        case 'branch':
          route = nextRoute(step, scope)
          continue
        case 'repeat': {
          const res = repeatStep(model, fi, f, step, (probe) => scopeFrom(r, probe, step))
          r = replaceTop(r, res.frame)
          route = res.route
          continue
        }
        case 'auto':
        case 'action':
        case 'function':
        case 'fetch_url': {
          setBusy(step.title ? text(step.title, scope) : 'Working…')
          const res = step.kind === 'auto' ? await runOps(env, f, step.do)
            : step.kind === 'action' ? await runAction(env, f, String(step.action || ''))
              : step.kind === 'function' ? await callFunction(env, f, step.id)
                : await fetchUrl(env, f, step.id)
          const ticket = (res as { poll?: unknown }).poll
          if (step.kind === 'function' && ticket) {
            // A started job the browser watches (PollStep): stop on the step
            // with its status address; the finish lands its answer.
            const entered = trail(enterStep(f, step.id), 'step', step.id)
            r = replaceTop(r, { ...entered, steps: { ...(entered.steps || {}), [step.id]: { poll: ticket } } })
            const started = (res as { result?: { message?: unknown } }).result?.message
            if (typeof started === 'string' && started) notify('info', started)
            return r
          }
          const said = (res as { result?: { message?: unknown } }).result?.message
          if (typeof said === 'string' && said && !hasBlocking(res.issues)) notify('success', said)
          r = replaceTop(r, res.frame)
          if (step.kind === 'action' && !hasBlocking(res.issues) && step.then?.length) {
            const more = await runOps(env, res.frame, step.then)
            r = replaceTop(r, more.frame)
            res.issues.push(...more.issues)
            if (more.route) res.route = more.route
          }
          if (hasBlocking(res.issues)) {
            const routed = issueRouting(step, res.issues)
            if (routed?.goto) { if (routed.message) notify('warning', routed.message); route = routeOf(routed.goto); continue }
            if (routed?.call) return fns.current.startCall(r, routed.call, step.id, true)
            if ((step.kind === 'fetch_url' || step.kind === 'function') && step.on_error) { route = routeOf(step.on_error); continue }
            return stopOn(r, step.id, res.issues)
          }
          scope = scopeFrom(r, top(r), step)
          route = res.route ?? routeAfter(model, top(r), step, scope)
          continue
        }
        case 'load': {
          if (f.dirty && !(await ask('Load another record? Changes not saved yet will be lost.', 'Load', 'Stay'))) {
            return stopOn(r, step.id, [{ message: 'Kept the record you were working on.', type: 'W' }])
          }
          setBusy('Looking the record up…')
          const found = await locate(env, f, step.match || {})
          if (found.found === 'cancelled') return stopOn(r, step.id, [{ message: 'No record was picked.', type: 'W' }])
          let cur = found.frame
          let branch: { set?: Record<string, unknown>; goto?: string } | null = null
          if (found.found === 'one') {
            branch = step.on_found || null
          } else if (found.issues.length && hasBlocking(found.issues)) {
            return stopOn(r, step.id, found.issues)
          } else {
            const miss = step.on_missing
            if (typeof miss === 'string') {
              if (miss === 'error' || miss.startsWith('error')) {
                const msg = text(miss.slice(6).trim(), scopeFrom(r, cur, step))
                return stopOn(r, step.id, [{ message: msg || 'No matching record.', code: 'WIZARD_NOT_FOUND', type: 'E' }])
              }
              cur = (await initAdd(env, cur)).frame
              branch = { goto: miss.replace(/^goto:/, '') }
            } else {
              cur = (await initAdd(env, cur)).frame
              branch = miss || null
            }
          }
          const sets = renderMap(branch?.set || {}, scopeFrom(r, cur, step))
          for (const [t, v] of Object.entries(sets.values)) {
            const s = await setValue(envFor(replaceTop(r, cur), idx, step.id), cur, t, v)
            cur = s.frame
          }
          r = replaceTop(r, cur)
          route = branch?.goto ? routeOf(branch.goto) : routeAfter(model, cur, step, scopeFrom(r, cur, step))
          continue
        }
        case 'save': {
          setBusy('Saving…')
          const out = await saveRecord(env, f, step)
          r = replaceTop(r, out.frame)
          if (out.status === 'failed') {
            const routed = issueRouting(step, out.issues)
            if (routed?.goto) { if (routed.message) notify('warning', routed.message); route = routeOf(routed.goto); continue }
            if (routed?.call) return fns.current.startCall(r, routed.call, step.id, true)
            return stopOn(r, step.id, out.issues)
          }
          setRefreshKey(k => k + 1)
          notify('success', out.message || (out.status === 'pending_approval' ? 'Submitted for approval.' : 'Saved'))
          scope = scopeFrom(r, top(r), step)
          route = (out.status === 'pending_approval' ? routeOf(step.on_pending_approval) : routeOf(step.on_saved))
            ?? routeAfter(model, top(r), step, scope)
          continue
        }
        case 'call': {
          if (step.ask) {
            // The question is the step's screen (CallStep).
            r = replaceTop(r, enterStep(f, step.id))
            setIssues([])
            return r
          }
          return fns.current.startCall(r, callBlockOf(step), step.id, false)
        }
        case 'handoff': {
          if (step.require_saved !== false && f.dirty) {
            return stopOn(r, step.id, [{ message: 'Save this record before moving on.', code: 'WIZARD_UNSAVED', type: 'E' }])
          }
          const { values } = renderMap(step.with || {}, scope)
          trackWizard('wizard_call', f.wizard, { to: step.wizard, handoff: true })
          clearMemoryRun(r.run_id)
          if (persisted.current) void serverRunStore(screenFor(r.wizard, r.run_id), r.wizard).discard(r.run_id).catch(() => undefined)
          if (host.startWizard) {
            host.startWizard(String(step.wizard || ''), values)
            setFinished({ message: '', outcome: 'completed' })
            setPhase('finished')
            return { ...r, status: 'completed' }
          }
          // No host navigation: replace this run with the target in place.
          const child = await ensureLoaded(String(step.wizard || ''))
          const fresh: WizardRun = {
            run_id: newRunId(), wizard: child.model.obj_name, status: 'active',
            stack: [newFrame(child.model, child.fi, { inputs: values })],
          }
          return fns.current.initFrame(fresh)
        }
        default:
          route = routeAfter(model, f, step, scope)
          continue
      }
    }
    notify('error', 'The wizard stopped: too many steps without a screen.')
    return r
  }

  /** The top frame finished: hand its outcome to the caller, or end the run. */
  const finish = async (start: WizardRun): Promise<WizardRun> => {
    let r = start
    const f = top(r)
    const { model, fi } = L(f.wizard)
    const scope = scopeOf(f, fi, { run: { run_id: r.run_id }, user: host.user })
    const outputs = computeOutputs(model, scope)
    const outcome = finishOutcome(f)
    // The approval the caller now waits on (its Work List task names it).
    if (outcome === 'pending_approval' && f.vars._approval_ref != null) outputs._approval_ref = f.vars._approval_ref
    r = replaceTop(r, trail(f, 'finish', f.cursor, outcome))
    trackWizard('wizard_finish', f.wizard, { outcome })
    if (assigneeRef.current) return handback(r, outcome, outputs)
    if (r.stack.length > 1) return fns.current.handleReturn(popCall(r, outcome, outputs))
    const message = text(model.finish?.message, scope)
    const then = String(model.finish?.then || 'close')
    if (then.startsWith('goto:')) {
      if (message) notify('success', message)
      return drive(r, routeOf(then))
    }
    clearMemoryRun(r.run_id)
    if (persisted.current) void serverRunStore(screenFor(r.wizard, r.run_id), r.wizard).discard(r.run_id).catch(() => undefined)
    persisted.current = false
    if (then === 'restart') {
      if (message) notify('success', message)
      const fresh: WizardRun = {
        run_id: newRunId(), wizard: model.obj_name, status: 'active',
        stack: [newFrame(model, fi, { inputs: f.input })],
      }
      return fns.current.initFrame(fresh)
    }
    if (model.mode === 'fill' && outcome === 'completed' && props.onFill) {
      // The screen that opened this wizard takes what the run changed.
      try { props.onFill(fillResult(f, fi.headerNo)) } catch (err) {
        notify('error', `Could not hand the values back: ${err instanceof Error ? err.message : String(err)}`)
      }
    }
    const done = { ...r, status: 'completed' as const }
    setFinished({ message: message || (outcome === 'pending_approval' ? 'Submitted for approval.' : 'Done.'), outcome })
    setPhase('finished')
    if (then === 'open_record' && f.txn_id && host.openRecord) host.openRecord(model.base_object, String(f.txn_id))
    else if (then === 'close' && host.close) host.close({ outcome, txnId: f.txn_id ?? null })
    return done
  }

  /** The assignee is done with the child (finished, cancelled, failed): hand
   *  it back to the requester. The server records the outcome on the parent
   *  frame and suspends the run for its owner to resume. */
  const handback = async (r: WizardRun, outcome: WizardCallOutcome, outputs: Record<string, unknown>): Promise<WizardRun> => {
    const f = top(r)
    const out = handbackOutcome(outcome)
    try {
      await serverRunStore(screenFor(f.wizard, r.run_id), f.wizard).handback({
        run_id: r.run_id, outcome: out, ...(out === 'completed' ? { outputs } : {}),
      })
    } catch (err) {
      notify('error', `Could not hand this back: ${err instanceof Error ? err.message : String(err)}`)
      return r
    }
    handedBack.current = true
    clearMemoryRun(r.run_id)
    trackWizard('wizard_call', f.wizard, { handback: out })
    setFinished({ message: 'Handed back to the requester.', outcome: out })
    setPhase('finished')
    host.close?.({ outcome: out, txnId: f.txn_id ?? null })
    return { ...r, status: 'completed' }
  }

  /** Push a child wizard (§8.2): checks, the child's frame with `with`
   *  filled, then its own start. `run_as` a role suspends to the Work List. */
  const startCall = async (start: WizardRun, block: WizardCallBlock, callingStep: string, viaTool: boolean): Promise<WizardRun> => {
    let r = start
    const parent = top(r)
    const pl = L(parent.wizard)
    const callStep = stepById(pl.model, callingStep)
    try {
      checkCall(r, pl.model, block.wizard)
    } catch (err) {
      return stopOn(r, parent.cursor || callingStep, [{ message: (err as Error).message, code: (err as WizardError).code, type: 'E' }])
    }
    const child = await ensureLoaded(block.wizard)
    const scope = scopeFrom(r, parent, callStep)
    const { values } = renderMap(block.with || {}, scope)
    // Where "same" and "next" resume: the step on screen, and (for a call
    // STEP) where the flow would have gone after it.
    const onScreen = parent.cursor && isInteractive(stepById(pl.model, parent.cursor)?.kind || 'auto')
      ? parent.cursor : (parent.back_stack || []).slice(-1)[0]?.cursor || parent.cursor
    let resumeNext: string | null | undefined
    const after = callStep ? routeAfter(pl.model, parent, callStep, scope) : null
    if (after) resumeNext = 'finish' in after ? null : after.goto
    const pending = {
      step_id: callingStep, resume_same: onScreen, resume_next: resumeNext,
      ...(viaTool ? { call: block } : {}),
    }
    const childFrame = newFrame(child.model, child.fi, { inputs: values, locked: block.lock_inputs })
    trackWizard('wizard_call', parent.wizard, { to: block.wizard })
    if (block.run_as && typeof block.run_as === 'object' && block.run_as.role) {
      // Completed by somebody holding the role, as a Work List task; this run
      // waits for it (the server's Work List provider hands the child over).
      r = pushCall(replaceTop(r, trail(parent, 'call', callingStep, `${block.wizard} as ${block.run_as.role}`)), childFrame,
        { ...pending, outcome: 'pending_assignee' })
      r = { ...r, status: 'waiting', waiting_on: assigneeWaitingOn(block, child.model.title) }
      commit(r, 'suspend')
      setPaused(`Waiting for ${block.run_as.role} to complete "${child.model.title || block.wizard}".`)
      setPhase('paused')
      return r
    }
    r = pushCall(replaceTop(r, trail(parent, 'call', callingStep, block.wizard)), childFrame, pending)
    commit(r, 'call')
    return fns.current.initFrame(r)
  }

  /** The child returned: `returns`, `refresh`, then resume / goto / suspend. */
  const handleReturn = async (start: WizardRun): Promise<WizardRun> => {
    let r = start
    const idx = r.stack.length - 1
    let f = top(r)
    const pl = L(f.wizard)
    const pending = f.pending_call
    if (!pending) return r
    const outcome = pending.outcome || 'cancelled'
    const outputs = pending.outputs || {}
    const block = pending.call || callBlockOf(stepById(pl.model, pending.step_id) || { id: '', kind: 'call' })
    const how = returnRoute(block, outcome)
    const callScope = { call: { outcome, outputs } }
    if (how.suspend && !isFinal(outcome)) {
      // An approval wait names the approval INSTANCE (the server's decision
      // hook finds the run by it) — never the record's id.
      r = { ...r, status: 'waiting', waiting_on: waitingOnFor(outcome, outputs) }
      commit(r, 'suspend')
      setPaused(outcome === 'pending_assignee'
        ? 'Paused — waiting for someone else to complete the other wizard.'
        : 'Paused — waiting for an approval. You will find this in your Work List.')
      setPhase('paused')
      return r
    }
    setBusy('Picking up where you left off…')
    const env = envFor(r, idx, pending.step_id, callScope)
    if (outcome === 'completed') {
      const ret = await runOps(env, f, returnSets(block, outputs))
      f = ret.frame
      if (how.refresh?.length) {
        const ref = await runOps(env, f, how.refresh)
        f = ref.frame
        if (ref.issues.length) showIssues(r, ref.issues)
      }
      f = boundary(f)
    }
    f = { ...f, pending_call: undefined }
    r = { ...replaceTop(r, f), status: 'active', waiting_on: null }
    setPaused(null)
    setPhase('ready')
    setRefreshKey(k => k + 1)
    if (how.message) notify(outcome === 'failed' ? 'error' : 'info', text(how.message, scopeFrom(r, f, null, callScope)))
    const target = how.goto ? routeOf(how.goto) : resumeTarget(how.resume, pending)
    commit(r, 'step')
    return drive(r, target || (pending.resume_same ? { goto: pending.resume_same } : null))
  }

  /** Fill a new frame's record (defaults / locate / inputs) and start it. */
  const initFrame = async (start: WizardRun): Promise<WizardRun> => {
    let r = start
    const idx = r.stack.length - 1
    let f = top(r)
    const { model, fi } = await ensureLoaded(f.wizard)
    const missing = missingInputs(model, f.input)
    if (missing.length) {
      r = replaceTop(r, { ...f, cursor: START_STEP })
      setStartInputs(missing)
      setPhase('start')
      return r
    }
    setStartInputs([])
    const env = envFor(r, idx, '')
    setBusy('Opening…')
    let firstStep = model.start
    if (model.mode === 'fill') {
      // The opening screen's buffer IS the record: no load, no defaults. Its
      // rows are marked so the finish hands back only what the run ADDED.
      if (idx === 0 && !props.onFill) {
        setError('This wizard fills in a screen — open it from that screen\'s button (in a drawer), not on its own.')
        setPhase('error')
        return r
      }
      const seed = idx === 0 ? props.seed : undefined
      const header = { ...(seed?.header || {}) }
      const details: Record<string, Row[]> = {}
      for (const [fno, rows] of Object.entries(seed?.details || {})) {
        details[fno] = (rows || []).map(row => ({ ...row, _wz_seed: true }))
      }
      f = { ...f, mode: 'fill', txn_id: null, record: { header, details }, fill_seed: { ...header }, line_index: null }
    } else if (model.mode === 'add') {
      f = (await initAdd(env, f)).frame
    } else {
      const found = model.locate?.match ? await locate(env, f, model.locate.match) : { frame: f, found: 'none' as const, issues: [] }
      if (found.found === 'one') f = found.frame
      else {
        const miss = String(model.locate?.on_missing || (model.mode === 'add_or_edit' ? 'add' : 'error'))
        if (found.found === 'cancelled' || (miss === 'error' && model.mode === 'edit')) {
          const msg = found.issues[0]?.message || (found.found === 'cancelled' ? 'No record was picked.' : 'No record this wizard can change matches — check the value, or whether the record is in a state this wizard works on.')
          setError(msg)
          setPhase('error')
          return r
        }
        f = (await initAdd(env, f)).frame
        if (miss.startsWith('goto:')) firstStep = miss.slice(5)
      }
    }
    // Launch inputs with a target land in declared order, each with its
    // column's item_change (as the open action's ordered `set`).
    for (const inp of model.inputs || []) {
      if (!inp.target || isBlank(f.input[inp.name])) continue
      const res = await setValue(envFor(replaceTop(r, f), idx, ''), f, inp.target, f.input[inp.name])
      f = res.frame
    }
    // Locked inputs read-only (presentation): their header targets' protect.
    const lockedTargets = (model.inputs || []).filter(i => i.target && (f.locked || []).includes(i.name))
    if (lockedTargets.length) {
      const protect = { ...(f.protect || {}) }
      for (const i of lockedTargets) {
        const t = parseTarget(String(i.target), fi)
        if (t && t.scope === 'header') protect[keyOf(fi, t.formNo, t.name)] = 1
      }
      f = { ...f, protect }
    }
    f = { ...f, dirty: false }
    r = replaceTop(r, f)
    setPhase('ready')
    return drive(r, firstStep ? { goto: firstStep } : null)
  }

  fns.current = { drive, finish, startCall, handleReturn, initFrame }

  /** Run a driver function under the busy flag and commit its result. */
  const act = useCallback(async (label: string, fn: (r: WizardRun) => Promise<WizardRun | void>) => {
    const r0 = runRef.current
    if (!r0) return
    await withBusy(label, async () => {
      try {
        const r = await fn(r0)
        if (r) commit(r)
      } catch (err) {
        notify('error', err instanceof Error ? err.message : String(err))
      } finally {
        setBusy(null)
      }
    })
  }, [withBusy, commit, notify])

  // ── Boot ───────────────────────────────────────────────────────────────

  useEffect(() => {
    let live = true
    ;(async () => {
      try {
        const root = await ensureLoaded(wizard)
        if (!live) return
        // A run the device kept (reload / crash while working offline).
        if (root.model.offline || isHostOffline(host)) hydrateDeviceRuns(host.project)
        let r: WizardRun | undefined
        if (resumeRunId) {
          r = readMemoryRun(resumeRunId)
          // A paused copy in this tab may be stale — the server is where an
          // approval decision or an assignee's hand-back lands.
          if (!r || r.status === 'waiting' || r.status === 'suspended') {
            const store = serverRunStore(screenFor(wizard, resumeRunId), wizard)
            try {
              const got = await store.load(resumeRunId)
              r = got.run
              assigneeRef.current = got.access === 'assignee'
              persisted.current = true
              // Only a `suspended` run is activated; a `waiting` one comes
              // back unchanged and keeps its banner.
              if (!assigneeRef.current && r.status === 'suspended') {
                const resumed = await store.resume(resumeRunId).catch(() => null)
                if (resumed) r = resumed
              }
            } catch (err) {
              if (!r) throw err
            }
          }
        } else {
          r = memoryRunFor(slot)
        }
        if (r) {
          // Every frame's model — except that an assignee runs (and may only
          // read) the top frame, and a frame that cannot load does not stop a
          // still-waiting run from showing its banner.
          const frames = assigneeRef.current ? [r.stack[r.stack.length - 1]] : r.stack
          for (let i = 0; i < frames.length; i++) {
            try {
              await ensureLoaded(frames[i].wizard)
            } catch (err) {
              const isTop = frames[i] === r.stack[r.stack.length - 1]
              if (isTop && r.status !== 'waiting') throw err
            }
          }
          r = await resumeRun(r)
          if (!live || !r) return
          commit(r)
          return
        }
        const fresh: WizardRun = {
          run_id: newRunId(), wizard: root.model.obj_name || wizard, status: 'active',
          stack: [newFrame(root.model, root.fi, { inputs })],
        }
        runRef.current = fresh
        busyRef.current = true
        try {
          const started = await fns.current.initFrame(fresh)
          if (live) commit(started)
        } finally {
          busyRef.current = false
          setBusy(null)
        }
      } catch (err) {
        if (!live) return
        setError(err instanceof Error ? err.message : String(err))
        setPhase('error')
      }
    })()
    return () => { live = false }
    // A different wizard / launch is a different run.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wizard, resumeRunId, slot])

  /** A restored run: frames whose model changed restart at `safe_resume`; a
   *  call whose outcome arrived meanwhile is routed; a still-waiting run
   *  shows its banner. */
  const resumeRun = async (stored: WizardRun): Promise<WizardRun> => {
    let r = stored
    const stack = r.stack.slice()
    for (let i = 0; i < stack.length; i++) {
      const fr = stack[i]
      const l = loaded.current.get(fr.wizard)
      if (!l) continue
      const { model } = l
      if (fr.fingerprint && model.fingerprint && fr.fingerprint !== model.fingerprint) {
        let nf: WizardFrame = { ...fr, fingerprint: model.fingerprint, model_changed: true, back_stack: [], cursor: model.safe_resume || model.start }
        if (fr.txn_id && !fr.dirty) {
          runRef.current = { ...r, stack }
          const loadedRec = await loadRecord(envFor({ ...r, stack }, i, ''), nf, String(fr.txn_id))
          if (!loadedRec.issues.length) nf = loadedRec.frame
        }
        stack[i] = nf
      }
    }
    r = { ...r, stack }
    runRef.current = r
    const f = top(r)
    if (f.model_changed) notify('warning', 'This wizard was changed while your run was paused — it restarts at a safe point.')
    if (assigneeRef.current) {
      // The child handed to this role: run it like any wizard — no banner.
      r = { ...r, status: 'active' }
      runRef.current = r
      setPaused(null)
      if (!f.cursor || f.cursor === START_STEP) {
        setPhase('ready')
        busyRef.current = true
        try { return await fns.current.initFrame(r) } finally { busyRef.current = false; setBusy(null) }
      }
    } else if (readyToReturn(r)) {
      setPhase('ready')
      busyRef.current = true
      try { return await fns.current.handleReturn(r) } finally { busyRef.current = false; setBusy(null) }
    }
    if (!assigneeRef.current && (r.status === 'waiting' || f.pending_call
      || (r.stack.length > 1 && r.stack[r.stack.length - 2].pending_call?.outcome === 'pending_assignee'))) {
      const w = r.waiting_on
      setPaused(w?.type === 'assignee'
        ? `Still waiting for ${w.role || 'someone else'} to complete "${w.title || w.wizard || 'the other wizard'}".`
        : 'Still waiting for an approval.')
      setPhase('paused')
      return r
    }
    if (f.cursor === START_STEP) return fns.current.initFrame(r)
    setPhase('ready')
    const { model } = L(f.wizard)
    const step = stepById(model, f.cursor)
    if (!step) return fns.current.drive(r, { goto: model.safe_resume || model.start })
    if (!isInteractive(step.kind) && step.kind !== 'call') return fns.current.drive(r, { goto: step.id })
    if (f.model_changed) return fns.current.drive(r, { goto: step.id })
    return { ...r, status: 'active' }
  }

  // ── Derived view ───────────────────────────────────────────────────────

  const frameIndex = run ? run.stack.length - 1 : 0
  const frame = run ? run.stack[frameIndex] : null
  const L0 = frame ? loaded.current.get(frame.wizard) || null : null
  const model = L0?.model || null
  const base = L0?.base || null
  const fi = L0?.fi || null
  const step = model && frame ? stepById(model, frame.cursor) : null
  const scope = useMemo(() => (run && frame && fi ? scopeOf(frame, fi, { step, run: { run_id: run.run_id }, user: host.user }) : null),
    [run, frame, fi, step, host.user])
  const screen = run && frame ? screenFor(frame.wizard, run.run_id) : null

  // Reset the per-step UI whenever a step is (re-)entered.
  const uiKey = frame ? `${frameIndex}|${frame.cursor}|${frame.history.length}|${(frame.back_stack || []).length}` : ''
  useEffect(() => {
    setUiState(prev => (prev.key === uiKey ? prev : EMPTY_UI(uiKey)))
    scanUndo.current = []
  }, [uiKey])
  const setUi = useCallback((patch: Partial<StepUi>) => setUiState(prev => ({ ...prev, ...patch })), [])

  const breadcrumb = useMemo(() => (run?.stack || []).map(f => ({
    wizard: f.wizard,
    title: loaded.current.get(f.wizard)?.model.title || f.wizard,
  })), [run])

  const path = useMemo(() => {
    if (!frame || !model) return []
    return frame.history.filter((id, i, all) => {
      const s = stepById(model, id)
      return !!s && isInteractive(s.kind) && all.indexOf(id) === i
    })
  }, [frame, model])

  // Tool visibility: `enabled_when` now, the target grant once (cached).
  useEffect(() => {
    if (!model || !host.canUse) return
    const tools = [...(model.tools || []), ...(step?.tools || []), ...(step?.row_tools || [])]
    for (const t of tools) {
      const target = toolTarget(t)
      if (!target) continue
      const key = `${target.type}:${target.name}:${target.right}`
      if (key in toolAllowed) continue
      host.canUse(target.type, target.name, target.right)
        .then(ok => setToolAllowed(prev => ({ ...prev, [key]: ok })))
        .catch(() => setToolAllowed(prev => ({ ...prev, [key]: false })))
    }
  }, [model, step, host, toolAllowed])

  const toolVisible = useCallback((tool: WizardTool): boolean => {
    if (tool.enabled_when && scope && !evalCondition(tool.enabled_when, scope, false)) return false
    const target = toolTarget(tool)
    if (!target || !host.canUse) return true
    return toolAllowed[`${target.type}:${target.name}:${target.right}`] === true
  }, [scope, toolAllowed, host.canUse])

  // ── Field edits ────────────────────────────────────────────────────────

  const change = useCallback((target: string, value: unknown, lineIndex?: number | null) => {
    const r = runRef.current
    if (!r) return
    const f = top(r)
    const l = loaded.current.get(f.wizard)
    if (!l) return
    const st = stepById(l.model, f.cursor)
    const form = String(st?.form_no || f.line_form || l.fi.lineForm)
    const t = parseTarget(target, l.fi, form)
    if (!t) return
    const at = (lineIndex != null && t.scope === 'line') ? { ...f, line_form: t.formNo, line_index: lineIndex } : f
    const written = writeValue(l, at, t, value)
    if (!written) return
    commit(replaceTop(r, lineIndex != null && t.scope === 'line' ? { ...written, line_form: f.line_form, line_index: f.line_index } : written))
    if (lineIndex != null && t.scope === 'line') {
      setUiState(prev => (prev.edited.includes(lineIndex) ? prev : { ...prev, edited: [...prev.edited, lineIndex] }))
    }
    setUiState(prev => {
      if (!prev.fieldErrors[target]) return prev
      const fe = { ...prev.fieldErrors }
      delete fe[target]
      return { ...prev, fieldErrors: fe }
    })
    if (t.scope === 'var') return
    const key = `${t.formNo}|${t.name}|${lineIndex ?? ''}`
    const fire = async () => {
      timers.current.delete(key)
      pendingChanges.current.delete(key)
      const cur = runRef.current
      if (!cur) return
      const cf = top(cur)
      const ll = loaded.current.get(cf.wizard)
      if (!ll || cf.wizard !== f.wizard) return
      const env = envFor(cur, cur.stack.length - 1)
      if (!itemChangeInvokable(env, t.formNo, t.name)) return
      const probe = (lineIndex != null && t.scope === 'line') ? { ...cf, line_form: t.formNo, line_index: lineIndex } : cf
      const res = await fireItemChange(env, probe, t)
      const latest = runRef.current
      if (!latest || top(latest).wizard !== cf.wizard) return
      const merged = (lineIndex != null && t.scope === 'line')
        ? { ...res.frame, line_form: cf.line_form, line_index: cf.line_index } : res.frame
      commit(replaceTop(latest, merged))
      if (hasBlocking(res.issues)) showIssues(latest, res.issues)
    }
    const old = timers.current.get(key)
    if (old) clearTimeout(old)
    timers.current.set(key, setTimeout(() => { void fire() }, 600))
    pendingChanges.current.set(key, fire)
  }, [commit, envFor, showIssues])

  /** Run every debounced item_change now (before Next / Save). */
  const flush = useCallback(async () => {
    const all = Array.from(pendingChanges.current.entries())
    for (const [key, fire] of all) {
      const t = timers.current.get(key)
      if (t) clearTimeout(t)
      await fire()
    }
  }, [])

  // ── Next / Back / Cancel ───────────────────────────────────────────────

  /** After an interactive step's input is accepted: `then`, `on_issue`, then
   *  where it goes. */
  const advance = useCallback(async (r0: WizardRun, opts: { pending?: Record<string, unknown>; extra?: Partial<WizardScope>; markLeft?: boolean; skipThen?: boolean } = {}) => {
    let r = r0
    const idx = r.stack.length - 1
    const f = opts.markLeft ? markLeft(top(r)) : top(r)
    const { model } = L(f.wizard)
    const st = stepById(model, f.cursor)
    if (!st) return r
    const env = envFor(r, idx, st.id, opts.extra)
    // A scan step's `then` is per SCAN (it already ran on every code read);
    // leaving the step (Next / the loop's Done) must not run it again with no
    // scan — a `match_line` would then add an empty line.
    const res = await runOps(env, f, opts.skipThen ? [] : st.then, { pending: opts.pending })
    r = replaceTop(r, res.frame)
    if (hasBlocking(res.issues)) {
      const routed = issueRouting(st, res.issues)
      if (routed?.goto) {
        if (routed.message) notify('warning', routed.message)
        return fns.current.drive(r, routeOf(routed.goto))
      }
      if (routed?.call) return fns.current.startCall(r, routed.call, st.id, true)
      showIssues(r, res.issues)
      return r
    }
    const scope2 = scopeFrom(r, top(r), st, opts.extra)
    return fns.current.drive(r, res.route ?? routeAfter(model, top(r), st, scope2))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [envFor, notify, showIssues, scopeFrom])

  const next = useCallback(async () => {
    await flush()
    const r0 = runRef.current
    if (!r0 || busyRef.current) return
    const f = top(r0)
    const l = loaded.current.get(f.wizard)
    if (!l) return
    const st = stepById(l.model, f.cursor)
    if (!st) return
    const sc = scopeFrom(r0, f, st)
    // Gates the step itself owns.
    if ((st.kind === 'info' || st.kind === 'show') && st.acknowledge && !ui.ack) {
      notify('warning', 'Tick the box to continue.')
      return
    }
    if (st.kind === 'confirm') {
      if (st.confirm && !ui.confirmed) {
        notify('warning', st.confirm.password ? 'Enter your password to confirm.' : 'Confirm to continue.')
        return
      }
      if (issues.some(i => i.type === 'E')) { notify('error', 'Fix the problems listed before continuing.'); return }
    }
    if (st.kind === 'print' && (st.required || st.file?.repository) && !ui.printed) {
      notify('warning', st.file?.repository ? 'File the report first.' : 'Print first.'); return
    }
    if (st.kind === 'sign' && st.required !== false && !ui.signed) { notify('warning', 'Sign first.'); return }
    if (st.kind === 'send' && st.required !== false && !ui.sent) { notify('warning', 'Send first.'); return }
    if (st.kind === 'scan' && !st.loop && !ui.scanned && !st.manual_entry) { notify('warning', 'Scan a code to continue.'); return }
    if (st.kind === 'input') {
      const miss = missingRequired(l.model, l.fi, st, f, sc)
      if (Object.keys(miss).length) { setUi({ fieldErrors: miss }); return }
    }
    if (st.kind === 'select' && st.target && !st.multi) {
      const t = parseTarget(st.target, l.fi, String(st.form_no || f.line_form || l.fi.lineForm))
      const v = t?.scope === 'var' ? f.vars[t.name] : t?.scope === 'header' ? readCol(f.record.header, l.fi, t.formNo, t.name) : readCol(sc.line, l.fi, t?.formNo || '', t?.name || '')
      if (isBlank(v) && f.steps?.[st.id] === undefined) { notify('warning', 'Pick one to continue.'); return }
    }
    if (st.kind === 'grid') {
      const errs = gridRequired(l.model, l.fi, st, f, (probe) => scopeFrom(r0, probe, st))
      if (Object.keys(errs).length) { setUi({ gridErrors: errs }); notify('error', 'Some lines need more information.'); return }
    }
    await act('Working…', async (r) => {
      let cur = r
      if (st.kind === 'grid' && ui.edited.length) {
        // Each edited row is finished the way the line form finishes rows.
        for (const i of ui.edited) {
          const cf = top(cur)
          const env = envFor(cur, cur.stack.length - 1, st.id)
          const res = await completeLine(env, { ...cf, line_form: String(st.form_no || l.model.line_form || l.fi.lineForm), line_index: i })
          cur = replaceTop(cur, { ...res.frame, line_form: cf.line_form, line_index: cf.line_index })
          if (hasBlocking(res.issues)) { showIssues(cur, res.issues); return cur }
        }
      }
      // Scans and multi-selects are their own effect: Back restores the entry.
      return advance(cur, { markLeft: st.kind !== 'scan' && !(st.kind === 'select' && st.multi), skipThen: st.kind === 'scan' })
    })
  }, [flush, scopeFrom, ui, issues, notify, setUi, act, envFor, showIssues, advance])

  const back = useCallback(() => {
    const r = runRef.current
    if (!r || busyRef.current) return
    const f = top(r)
    const l = loaded.current.get(f.wizard)
    if (!l) return
    const prev = machineBack(l.model, f)
    if (!prev) return
    setIssues([])
    commit(replaceTop(r, prev))
  }, [commit])

  const cancel = useCallback(async () => {
    const r = runRef.current
    if (!r || busyRef.current) return
    if (assigneeRef.current) {
      // An assignee cannot discard the requester's run: cancelling hands it back.
      if (!(await ask('Hand this back without completing it?', 'Hand back', 'Stay'))) return
      await act('Handing back…', async (cur) => handback(cur, 'cancelled', {}))
      return
    }
    if (r.stack.length > 1) {
      // Cancelling a child returns "cancelled" to the caller — never discards it.
      await act('Returning…', async (cur) => fns.current.handleReturn(popCall(cur, 'cancelled', {})))
      return
    }
    const f = top(r)
    if (f.dirty && !(await ask('Leave this wizard? What you entered has not been saved.', 'Leave', 'Stay'))) return
    clearMemoryRun(r.run_id)
    if (persisted.current) void serverRunStore(screenFor(r.wizard, r.run_id), r.wizard).discard(r.run_id).catch(() => undefined)
    persisted.current = false
    commit({ ...r, status: 'cancelled' })
    clearMemoryRun(r.run_id)
    setFinished({ message: 'Cancelled.', outcome: 'cancelled' })
    setPhase('finished')
    host.close?.({ outcome: 'cancelled', txnId: f.txn_id ?? null })
  }, [act, ask, commit, host, screenFor])

  /** Save the run for later (server runs) and step away. */
  const pause = useCallback(async () => {
    const r = runRef.current
    if (!r) return
    const suspended = { ...r, status: 'suspended' as const, title: r.title || runTitle(r) }
    runRef.current = suspended
    setRunState(suspended)
    writeMemoryRun(suspended, slot)
    if (assigneeRef.current || loaded.current.get(r.wizard)?.model.suspend === 'server') {
      persisted.current = true
      // An assignee saves progress through the child (the server keeps the
      // run's owner, status and waiting_on).
      const via = assigneeRef.current ? r.stack[r.stack.length - 1].wizard : r.wizard
      try {
        await serverRunStore(screenFor(via, r.run_id), via).save(assigneeRef.current ? r : suspended)
        setPaused('Saved for later — resume it from your Work List.')
      } catch (err) {
        notify('error', `Could not save the run: ${err instanceof Error ? err.message : String(err)}`)
        return
      }
    } else {
      setPaused('Paused — it stays open in this tab until you come back to it.')
    }
    setPhase('paused')
  }, [slot, screenFor, notify])

  const handBackFailed = useCallback(async () => {
    if (!assigneeRef.current || !runRef.current) return
    const r0 = runRef.current
    await withBusy('Handing back…', async () => {
      const r = await handback(r0, 'failed', {})
      commit(r)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [withBusy, commit])

  const resume = useCallback(() => {
    const r = runRef.current
    if (!r || r.status === 'waiting') return
    commit({ ...r, status: 'active' })
    setPaused(null)
    setPhase('ready')
  }, [commit])

  const restart = useCallback(async () => {
    const r = runRef.current
    const root = loaded.current.get(wizard)
    if (!root) return
    if (r) clearMemoryRun(r.run_id)
    persisted.current = false
    setFinished(null)
    setPaused(null)
    setError(null)
    setIssues([])
    const fresh: WizardRun = {
      run_id: newRunId(), wizard: root.model.obj_name, status: 'active',
      stack: [newFrame(root.model, root.fi, { inputs })],
    }
    runRef.current = fresh
    setPhase('loading')
    await act('Opening…', async () => fns.current.initFrame(fresh))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wizard, inputs, act])

  const jumpTo = useCallback(async (stepId: string, line?: { form: string; index: number | null } | null) => {
    await act('Working…', async (r) => {
      let cur = r
      if (line && line.index != null) cur = replaceTop(cur, { ...top(cur), line_form: line.form, line_index: line.index })
      setIssues([])
      return fns.current.drive(cur, { goto: stepId })
    })
  }, [act])

  const retry = useCallback(async () => {
    const r = runRef.current
    if (!r) return
    const f = top(r)
    await act('Working…', async (cur) => {
      // Drop the stop's own snapshot: the retried step re-enters cleanly.
      const fr = top(cur)
      const trimmed = replaceTop(cur, { ...fr, back_stack: (fr.back_stack || []).slice(0, -1) })
      return fns.current.drive(trimmed, { goto: f.cursor })
    })
  }, [act])

  const redirectOpen = useCallback(async (): Promise<WizardRedirectStartResponse> => {
    const r = runRef.current
    if (!r) return { ok: false, error: 'The wizard is not running.' }
    const f = top(r)
    const st = stepById(L(f.wizard).model, f.cursor)
    if (!st || st.kind !== 'redirect') return { ok: false, error: 'This step does not open another site.' }
    trackWizard('wizard_redirect', f.wizard, { step: st.id })
    return redirectStart(envFor(r, r.stack.length - 1, st.id), f, st.id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [envFor])

  const redirectPoll = useCallback(async (): Promise<'none' | 'waiting' | 'expired' | 'done' | 'failed'> => {
    const r0 = runRef.current
    if (!r0 || busyRef.current) return 'waiting'
    const f0 = top(r0)
    const st = stepById(L(f0.wizard).model, f0.cursor)
    if (!st || st.kind !== 'redirect') return 'none'
    const res = await redirectStatus(envFor(r0, r0.stack.length - 1, st.id), f0, st.id)
    if (res.state === 'waiting' || res.state === 'none') {
      if (res.failed && res.issues.length) showIssues(r0, res.issues)
      return res.state
    }
    let outcome: 'done' | 'failed' | 'expired' = res.state === 'expired' ? 'expired' : 'done'
    await act('Reading the reply…', async (r) => {
      // Only if the run is still on this step (a Back / Cancel meanwhile wins).
      if (top(r).cursor !== st.id) return r
      const cur = replaceTop(r, res.frame)
      if (hasBlocking(res.issues)) {
        const routed = issueRouting(st, res.issues)
        if (routed?.goto) { if (routed.message) notify('warning', routed.message); return fns.current.drive(cur, routeOf(routed.goto)) }
        if (st.on_error && res.state !== 'expired') return fns.current.drive(cur, routeOf(st.on_error))
        if (outcome !== 'expired') outcome = 'failed'
        showIssues(cur, res.issues)
        return cur
      }
      const said = (res as { result?: { message?: unknown } }).result?.message
      if (typeof said === 'string' && said) notify('success', said)
      return advance(cur, { markLeft: true })
    })
    return outcome
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [envFor, act, advance, showIssues, notify])

  const pollJob = useCallback(async (server: boolean): Promise<'waiting' | 'done' | 'failed' | 'expired'> => {
    const r0 = runRef.current
    if (!r0 || busyRef.current) return 'waiting'
    const f0 = top(r0)
    const st = stepById(L(f0.wizard).model, f0.cursor)
    if (!st || st.kind !== 'function') return 'failed'
    const res = await pollStep(envFor(r0, r0.stack.length - 1, st.id), f0, st.id, server)
    if (res.state === 'waiting') return 'waiting'
    let outcome: 'done' | 'failed' | 'expired' = res.state === 'expired' ? 'expired' : 'done'
    await act('Reading the result…', async (r) => {
      if (top(r).cursor !== st.id) return r
      // The job is over either way: the step no longer waits on it.
      const fr = res.frame
      const steps = { ...(fr.steps || {}) }
      delete steps[st.id]
      const cur = replaceTop(r, { ...fr, steps })
      if (hasBlocking(res.issues)) {
        const routed = issueRouting(st, res.issues)
        if (routed?.goto) { if (routed.message) notify('warning', routed.message); return fns.current.drive(cur, routeOf(routed.goto)) }
        if (st.on_error) return fns.current.drive(cur, routeOf(st.on_error))
        if (outcome !== 'expired') outcome = 'failed'
        showIssues(cur, res.issues)
        return cur
      }
      const said = (res as { result?: { message?: unknown } }).result?.message
      if (typeof said === 'string' && said) notify('success', said)
      return advance(cur, { markLeft: true })
    })
    return outcome
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [envFor, act, advance, showIssues, notify])

  // ── Step actions ───────────────────────────────────────────────────────

  const submitStart = useCallback(async (values: Record<string, unknown>) => {
    await act('Opening…', async (r) => {
      const f = top(r)
      const withInputs = replaceTop(r, { ...f, input: { ...f.input, ...values } })
      return fns.current.initFrame(withInputs)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [act])

  const select = useCallback(async (rows: Row[]) => {
    const r0 = runRef.current
    if (!r0 || !rows.length) return
    const f0 = top(r0)
    const l = L(f0.wizard)
    const st = stepById(l.model, f0.cursor)
    if (!st || st.kind !== 'select') return
    const form = String(st.form_no || l.model.line_form || l.fi.lineForm)
    await act('Working…', async (r) => {
      let f = top(r)
      const idx = r.stack.length - 1
      const env = () => envFor(replaceTop(r, f), idx, st.id)
      const issuesOut: WizardIssue[] = []
      const src = st.source || {}
      if (src.records) {
        const res = await loadRecord(env(), f, String(rows[0]._txn_id))
        f = res.frame
        issuesOut.push(...res.issues)
      } else if (src.line_source) {
        const exec = (a: string, p: Record<string, unknown>) => env().rt.execute(l.base.obj_name, a, p)
                const api = lineEntryApi(exec, { formNo: form, headerValues: f.record.header, txnId: f.txn_id || undefined, lines: f.record.details[form] || [] })
        try {
          const applied = await api.apply(src.line_source, rows.map(x => ({ _src_key: String(x._src_key) })))
          for (const lr of applied.rows || []) {
            const added = await addLine(env(), f, form, {}, { row: toBufferRow(lr) })
            f = added.frame
          }
        } catch (err) {
          issuesOut.push(...issuesFromError(err))
        }
      } else if (st.multi) {
        for (const row of rows) {
          const t = st.target ? parseTarget(st.target, l.fi, form) : null
          const set: Record<string, unknown> = {}
          if (t && t.scope === 'line') set[t.name] = row.data
          for (const [target, col] of Object.entries(st.map || {})) {
            const tt = parseTarget(target, l.fi, form)
            if (tt && tt.scope === 'line') set[tt.name] = row[col as string] ?? readCol(row, l.fi, '', String(col))
          }
          const added = await addLine(env(), f, t?.formNo || form, set)
          f = added.frame
          issuesOut.push(...added.issues)
          if (hasBlocking(added.issues)) break
        }
      } else {
        const row = rows[0]
        if (st.target) {
          const res = await setValue(env(), f, st.target, row.data)
          f = res.frame
          issuesOut.push(...res.issues)
        }
        for (const [target, col] of Object.entries((st.map || {}) as Record<string, unknown>)) {
          if (hasBlocking(issuesOut)) break
          const v = row[String(col)] ?? readCol(row, l.fi, '', String(col))
          const res = await setValue(env(), f, target, v)
          f = res.frame
          issuesOut.push(...res.issues)
        }
      }
      f = { ...f, steps: { ...(f.steps || {}), [st.id]: st.multi ? rows.map(x => x.data ?? x._txn_id) : (rows[0].data ?? rows[0]._txn_id) } }
      let cur = replaceTop(r, f)
      if (hasBlocking(issuesOut)) { showIssues(cur, issuesOut); return cur }
      const autoAdvance = st.auto_advance ?? !st.multi
      if (autoAdvance) cur = await advance(cur, { markLeft: !st.multi })
      return cur
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [act, envFor, showIssues, advance])

  const scan = useCallback(async (raw: string): Promise<boolean> => {
    const textIn = String(raw || '').trim()
    const r0 = runRef.current
    if (!textIn || !r0) return false
    const f0 = top(r0)
    const l = L(f0.wizard)
    const st = stepById(l.model, f0.cursor)
    if (!st || st.kind !== 'scan') return false
    let ok = false
    const log = (good: boolean, message: string) => {
      const entry = { id: Date.now() + Math.random(), at: Date.now(), text: textIn, ok: good, message }
      lastScanRef.current = entry
      setUiState(prev => ({ ...prev, scanned: prev.scanned || good, scanLog: [entry, ...prev.scanLog].slice(0, 8) }))
    }
    await act('Reading…', async (r) => {
      const idx = r.stack.length - 1
      const f = top(r)
      const env = envFor(r, idx, st.id)
      let parsed
      try {
        // Offline there is no server to parse with: the same rules, read from
        // the model the device holds, parsed here (scanParse.ts, fixture parity).
        parsed = isHostOffline(host)
          ? parseScanInScope(st, textIn, scopeOf(f, l.fi, { step: st, run: { run_id: r.run_id }, user: host.user }))
          : await scanParse(env, f, st.id, textIn)
      } catch (err) {
        const list = issuesFromError(err)
        if (st.feedback?.error !== 'none') buzz()
        log(false, list[0]?.message || 'Could not read that code')
        return r
      }
      trackWizard('wizard_scan', f.wizard, { step: st.id, ok: !!parsed?.ok && parsed.expect_ok !== false })
      if (!parsed?.ok || parsed.expect_ok === false) {
        if (st.feedback?.error !== 'none') buzz()
        log(false, parsed?.message || (parsed?.ok ? 'That is not the expected code' : 'That code was not recognised'))
        return r
      }
      scanUndo.current = [f, ...scanUndo.current].slice(0, 20)
      const form = String(st.form_no || l.model.line_form || l.fi.lineForm)
      const { now, pending } = splitSets(l, parsed.sets || [], stagesLine(st.then), form)
      const extra = { scan: { ...(parsed.fields || {}), rule: parsed.rule, value: textIn } }
      const env2 = envFor(r, idx, st.id, extra)
      let cur = f
      const list: WizardIssue[] = []
      for (const s of now) {
        const res = await setValue(env2, cur, s.target, s.value)
        cur = res.frame
        list.push(...res.issues)
        if (hasBlocking(list)) break
      }
      let route: Route | null | undefined
      if (!hasBlocking(list)) {
        const res = await runOps(env2, cur, st.then, { pending })
        cur = res.frame
        list.push(...res.issues)
        route = res.route
      }
      cur = trail({ ...cur, steps: { ...(cur.steps || {}), [st.id]: textIn } }, 'scan', st.id, textIn)
      if (hasBlocking(list)) {
        const routed = issueRouting(st, list)
        if (st.feedback?.error !== 'none') buzz()
        if (routed?.goto) { if (routed.message) notify('warning', routed.message); return fns.current.drive(replaceTop(r, cur), routeOf(routed.goto)) }
        log(false, list[0]?.message || 'Refused')
        // A refused scan leaves the record as it was.
        return replaceTop(r, trail(f, 'issue', st.id, list[0]?.message))
      }
      ok = true
      if (st.feedback?.ok !== 'none') beep()
      // An unnamed rule is known by its index ("0") — not worth showing.
      log(true, parsed.rule && !/^\d+$/.test(parsed.rule) ? `Read (${parsed.rule})` : 'Read')
      let next = replaceTop(r, cur)
      if (route) return fns.current.drive(next, route)
      const sc = scopeFrom(next, cur, st, extra)
      if (st.loop) {
        const until = st.loop.until && !String(st.loop.until).startsWith('button:') ? st.loop.until : undefined
        const done = (until && evalCondition(until, sc, false)) || (st.loop.or_when && evalCondition(st.loop.or_when, sc, false))
        if (done) next = await fns.current.drive(next, routeAfter(l.model, cur, st, sc))
        return next
      }
      if (st.auto_advance !== false) next = await fns.current.drive(next, routeAfter(l.model, cur, st, sc))
      return next
    })
    return ok
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [act, envFor, notify, scopeFrom])

  const undoScan = useCallback(() => {
    const r = runRef.current
    const prev = scanUndo.current.shift()
    if (!r || !prev || top(r).cursor !== prev.cursor) return
    commit(replaceTop(r, prev))
    // Drop the log line of the scan just undone — the newest ACCEPTED one; a
    // refused scan after it changed nothing and stays listed.
    setUiState(p => {
      const at = p.scanLog.findIndex(e => e.ok)
      return at < 0 ? p : { ...p, scanLog: p.scanLog.filter((_, i) => i !== at) }
    })
  }, [commit])

  // Scans wait their turn: each runs on the record the one before it left,
  // and one arriving while another is in flight is queued, not dropped.
  const scanFn = useRef(scan)
  scanFn.current = scan
  const scanQueue = useRef<SerialQueue<string, boolean> | null>(null)
  if (!scanQueue.current) {
    scanQueue.current = createSerialQueue(async (t: string) => {
      // Something else holds the runner (a Next pressed meanwhile): wait for
      // it rather than lose the scan.
      for (let i = 0; busyRef.current && i < 600; i++) await new Promise(res => setTimeout(res, 50))
      // The scan before it moved the wizard on to a step that takes none.
      const r = runRef.current
      const f = r ? top(r) : null
      const l = f ? loaded.current.get(f.wizard) : null
      if (!f || !l || stepById(l.model, f.cursor)?.kind !== 'scan') {
        notify('warning', `"${t}" was not used — this step does not take a scan.`)
        return false
      }
      return scanFn.current(t)
    }, false)
  }
  const enqueueScan = useCallback((t: string) => scanQueue.current!.push(t), [])
  const lastScan = useCallback(() => lastScanRef.current, [])

  const confirmPassword = useCallback(async (password: string): Promise<WizardConfirmResponse> => {
    const r = runRef.current
    if (!r) return { ok: false, verified: false }
    const f = top(r)
    const l = L(f.wizard)
    const st = stepById(l.model, f.cursor)
    if (!st || st.kind !== 'confirm' || !st.confirm?.password) return { ok: false, verified: false }
    if (isHostOffline(host)) {
      return { ok: false, verified: false, code: 'WIZARD_CONFIRM_UNAVAILABLE', message: 'Confirming with your password needs a connection.' }
    }
    const res = await confirmPasswordOp({ rt: screenFor(f.wizard, r.run_id), model: l.model }, st.id, password)
    const cur = runRef.current
    if (res.verified && cur && top(cur).cursor === st.id) {
      setUiState(p => ({ ...p, confirmed: true }))
      commit(replaceTop(cur, trail(top(cur), 'step', st.id, 'confirmed with password')))
    }
    return res
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [host, screenFor, commit])

  const setCells = useCallback(async (cells: GridCellSet[], addRows = 0) => {
    if (!cells.length && !addRows) return
    // A cell typed a moment ago lands first.
    await flush()
    await act(cells.length > 1 ? 'Filling…' : 'Working…', async (r) => {
      let cur = r
      const f0 = top(cur)
      const l = L(f0.wizard)
      const st = stepById(l.model, f0.cursor)
      if (!st || st.kind !== 'grid') return cur
      const form = String(st.form_no || l.model.line_form || l.fi.lineForm)
      const editable = new Set((st.editable || []).map(c => keyOf(l.fi, form, c)))
      const idx = cur.stack.length - 1
      const issuesOut: WizardIssue[] = []
      const added: number[] = []
      for (let k = 0; k < addRows && st.allow_add; k++) {
        const res = await addLine(envFor(cur, idx, st.id), top(cur), form)
        cur = replaceTop(cur, res.frame)
        if (res.frame.line_index != null) added.push(res.frame.line_index)
        issuesOut.push(...res.issues)
        if (hasBlocking(res.issues)) break
      }
      const touched = new Set<number>(added)
      for (const c of cells) {
        const at = typeof c.line === 'number' ? c.line : added[c.line.add]
        const t = parseTarget(c.column, l.fi, form)
        // Only the step's editable columns, on a live line.
        if (at == null || !t || t.scope !== 'line' || !editable.has(keyOf(l.fi, form, t.name))) continue
        const cf = top(cur)
        if (!cf.record.details[form]?.[at] || cf.record.details[form][at]._deleted) continue
        const res = await setValue(envFor(cur, idx, st.id), { ...cf, line_form: form, line_index: at }, t, c.value)
        cur = replaceTop(cur, { ...res.frame, line_form: cf.line_form, line_index: cf.line_index })
        touched.add(at)
        issuesOut.push(...res.issues)
      }
      cur = replaceTop(cur, { ...top(cur), line_form: f0.line_form, line_index: f0.line_index })
      setUiState(prev => ({ ...prev, edited: Array.from(new Set([...prev.edited, ...touched])) }))
      if (hasBlocking(issuesOut)) showIssues(cur, issuesOut)
      return cur
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flush, act, envFor, showIssues])

  const answerCall = useCallback(async (yes: boolean) => {
    await act('Working…', async (r) => {
      const f = top(r)
      const l = L(f.wizard)
      const st = stepById(l.model, f.cursor)
      if (!st || st.kind !== 'call') return r
      if (yes) return fns.current.startCall(r, callBlockOf(st), st.id, false)
      // "No" routes as `cancelled`.
      const block = callBlockOf(st)
      const how = returnRoute(block, 'cancelled')
      const sc = scopeFrom(r, f, st)
      const after = routeAfter(l.model, f, st, sc)
      const prevCursor = (f.back_stack || []).slice(-2)[0]?.cursor
      const target = how.goto ? routeOf(how.goto)
        : how.resume === 'next' ? after
          : how.resume && how.resume !== 'same' ? routeOf(how.resume)
            : prevCursor ? { goto: prevCursor } : after
      return fns.current.drive(r, target)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [act, scopeFrom])

  const doPrint = useCallback(async (block: { report?: WizardStep['report']; output_format?: string; delivery?: WizardStep['delivery']; copies?: number; printer?: string; title?: string; required?: boolean }, sc: WizardScope) => {
    if (!block.report?.obj_name) { notify('error', 'No report is set for printing.'); return false }
    if (!host.printReport) { notify('error', 'Printing is not available here.'); return false }
    const { values } = renderMap(block.report.param_map || {}, sc)
    await host.printReport({
      objName: block.report.obj_name, params: values,
      outputFormat: block.report.output_format || block.output_format,
      delivery: block.delivery || 'print_dialog', copies: block.copies, printer: block.printer, title: block.title,
      required: !!block.required,
      ...(runRef.current ? { via: { obj_name: top(runRef.current).wizard, run_id: runRef.current.run_id } } : {}),
    })
    return true
  }, [host, notify])

  const print = useCallback(async () => {
    const r = runRef.current
    if (!r) return
    const f = top(r)
    const l = L(f.wizard)
    const st = stepById(l.model, f.cursor)
    if (!st) return
    const filing = !!st.file?.repository
    await withBusy(filing ? 'Filing…' : 'Printing…', async () => {
      try {
        if (filing) {
          // File FIRST: the repository copy is the record. The server renders
          // it again from the stored record and the model's own settings.
          const res = await documentOp({ rt: screenFor(f.wizard, r.run_id), model: l.model },
            'wizard_file', st.id, f.txn_id, frameValues(scopeFrom(r, f, st)))
          if (!res.ok || !res.filed) {
            notify('error', `Filing failed: ${res.error || 'the report was not filed'}`)
            return
          }
          const cur0 = runRef.current!
          const t0 = top(cur0)
          commit(replaceTop(cur0, trail({ ...t0, vars: { ...t0.vars, [res.var || 'filed']: res.filed } },
            'file', st.id, `${res.filed.repository}:${res.filed.document_id}`)))
          trackWizard('wizard_file', f.wizard, { step: st.id, report: st.report?.obj_name, repository: res.filed.repository })
          notify('success', `Filed in ${res.filed.repository}${res.filed.new_document ? '' : ' as a new version'}.`)
        }
        const ok = st.delivery === 'none' ? true
          : await doPrint({ ...st, title: st.title ? text(st.title, scopeFrom(r, f, st)) : undefined }, scopeFrom(r, f, st))
        if (!ok) return
        trackWizard('wizard_print', f.wizard, { step: st.id, report: st.report?.obj_name })
        const cur = runRef.current!
        // A print is a fact on paper: Back does not cross it.
        commit(replaceTop(cur, boundary(trail(top(cur), 'print', st.id, st.report?.obj_name))))
        setUiState(p => ({ ...p, printed: true }))
      } catch (err) {
        notify('error', `Printing failed: ${err instanceof Error ? err.message : String(err)}`)
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [withBusy, doPrint, scopeFrom, commit, notify])

  const send = useCallback(async () => {
    const r = runRef.current
    if (!r) return
    const f = top(r)
    const l = L(f.wizard)
    const st = stepById(l.model, f.cursor)
    if (!st || st.kind !== 'send') return
    await withBusy('Sending…', async () => {
      const res = await documentOp({ rt: screenFor(f.wizard, r.run_id), model: l.model },
        'wizard_send', st.id, f.txn_id, frameValues(scopeFrom(r, f, st)))
      if (!res.ok) { notify('error', `Sending failed: ${res.error || 'nothing was sent'}`); return }
      trackWizard('wizard_send', f.wizard, { step: st.id, action: st.action })
      const cur = runRef.current!
      // A message, once queued, is out of the wizard's hands: Back does not cross it.
      commit(replaceTop(cur, boundary(trail(top(cur), 'send', st.id, st.action))))
      setUiState(p => ({ ...p, sent: true }))
      notify(res.queued ? 'success' : 'warning', res.queued
        ? `Sent — ${res.queued} message${res.queued === 1 ? '' : 's'} on the way.`
        : 'Nothing was sent: no recipient could be reached.')
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [withBusy, scopeFrom, commit, notify])

  const runTool = useCallback(async (tool: WizardTool, lineIndex?: number | null) => {
    const r0 = runRef.current
    if (!r0) return
    let r = r0
    const f0 = top(r)
    const l = L(f0.wizard)
    const st = stepById(l.model, f0.cursor)
    if (lineIndex != null) {
      r = replaceTop(r, { ...f0, line_form: String(st?.form_no || l.model.line_form || l.fi.lineForm), line_index: lineIndex })
      commit(r)
    }
    const f = top(r)
    const sc = scopeFrom(r, f, st)
    switch (tool.kind) {
      case 'open': {
        const b = tool.open
        if (!b || !host.openObject) { notify('error', 'Opening another screen is not available here.'); return }
        const rendered: OpenActionBinding = {
          ...b,
          match: b.match ? renderMap(b.match, sc).values : undefined,
          params: b.params ? Object.fromEntries(Object.entries(b.params).map(([k, v]) => [k,
            (v && typeof v === 'object' && 'value' in (v as Record<string, unknown>))
              ? { ...(v as Record<string, unknown>), value: render((v as { value: unknown }).value, sc) }
              : render(v, sc)])) : undefined,
          set: b.set?.map(s => ({ ...s, value: render(s.value, sc) })),
          presentation: b.presentation || 'drawer',
        }
        const req = buildOpenActionRequest(tool.label, rendered, l.base.obj_name, f.record.header, f.record.header,
          () => setRefreshKey(k => k + 1))
        if (typeof req === 'string') notify('error', req)
        else host.openObject(req)
        return
      }
      case 'assistant': {
        const a = tool.assistant
        if (!a || !host.openAssistant) { notify('error', 'The Assistant is not available here.'); return }
        host.openAssistant({
          title: a.title || tool.label,
          mode: a.mode === 'prompt' ? 'prompt' : 'intent',
          intentId: a.intent_id,
          parameters: renderMap(a.param_map || {}, sc).values,
          prompt: a.prompt ? text(a.prompt, sc) : undefined,
        })
        return
      }
      case 'print':
        await withBusy('Printing…', async () => {
          try {
            if (await doPrint({ report: tool.report, delivery: tool.delivery, copies: tool.copies, printer: tool.printer, title: tool.label }, sc)) {
              trackWizard('wizard_print', f.wizard, { tool: tool.label, report: tool.report?.obj_name })
              const cur = runRef.current!
              commit(replaceTop(cur, trail(top(cur), 'print', f.cursor, tool.report?.obj_name)))
            }
          } catch (err) {
            notify('error', `Printing failed: ${err instanceof Error ? err.message : String(err)}`)
          }
        })
        return
      case 'action':
        await act('Working…', async (cur) => {
          const res = await runAction(envFor(cur, cur.stack.length - 1, f.cursor), top(cur), String(tool.action || ''))
          const nr = replaceTop(cur, res.frame)
          if (res.issues.length) {
            const routed = issueRouting(st, res.issues)
            if (routed?.goto) return fns.current.drive(nr, routeOf(routed.goto))
            if (hasBlocking(res.issues)) showIssues(nr, res.issues)
          }
          setRefreshKey(k => k + 1)
          return nr
        })
        return
      case 'goto':
        if (tool.goto) await jumpTo(tool.goto)
        return
      case 'call':
        if (tool.call) await act('Opening…', async (cur) => fns.current.startCall(cur, tool.call!, f.cursor, true))
        return
      default:
        return
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commit, scopeFrom, host, notify, withBusy, doPrint, act, envFor, showIssues, jumpTo])

  const pickRow = useCallback(async (panel: WizardContextPanel, row: Row) => {
    const pr = panel.pick_row
    if (!pr) return
    await act('Working…', async (r) => {
      let f = top(r)
      const env = envFor(r, r.stack.length - 1)
      for (const [target, col] of Object.entries(pr.set || {})) {
        const res = await setValue(env, f, target, readCol(row, buildFormIndex(null), '', col) ?? row[col])
        f = res.frame
        if (hasBlocking(res.issues)) { showIssues(replaceTop(r, f), res.issues); return replaceTop(r, f) }
      }
      const cur = replaceTop(r, f)
      const route = pr.then ? routeOf(pr.then) : null
      return route ? fns.current.drive(cur, route) : cur
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [act, envFor, showIssues])

  const addGridLine = useCallback(async () => {
    await act('Adding…', async (r) => {
      const f = top(r)
      const l = L(f.wizard)
      const st = stepById(l.model, f.cursor)
      const form = String(st?.form_no || l.model.line_form || l.fi.lineForm)
      const res = await addLine(envFor(r, r.stack.length - 1, st?.id), f, form)
      if (hasBlocking(res.issues)) showIssues(r, res.issues)
      return replaceTop(r, res.frame)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [act, envFor, showIssues])

  const deleteGridLine = useCallback((index: number) => {
    const r = runRef.current
    if (!r) return
    const f = top(r)
    const l = L(f.wizard)
    const st = stepById(l.model, f.cursor)
    const form = String(st?.form_no || l.model.line_form || l.fi.lineForm)
    const removed = deleteLine({ ...f, line_form: form, line_index: index })
    commit(replaceTop(r, { ...removed, line_form: f.line_form, line_index: f.line_index === index ? null : f.line_index }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commit])

  const linesOf = useCallback((form: string) => {
    if (!frame) return []
    return (frame.record.details[form] || []).map((row, index) => ({ index, row })).filter(x => !x.row._deleted)
  }, [frame])

  // ── Modals ─────────────────────────────────────────────────────────────

  const modals = createElement('div', { style: { display: 'contents' } },
    warningsModal, reasonModal, actionConfirmModal,
    htmlModal ? createElement(HtmlResultModal, { title: htmlModal.title, html: htmlModal.html, onClose: () => setHtmlModal(null) }) : null,
    chooser ? createElement(ChooseDialog, {
      ...chooser,
      onDone: (rows: Row[] | null) => { chooser.resolve(rows); setChooser(null) },
    }) : null,
    confirmer ? createElement(ConfirmDialog, {
      message: confirmer.message, yes: confirmer.yes, no: confirmer.no,
      onDone: (ok: boolean) => { confirmer.resolve(ok); setConfirmer(null) },
    }) : null,
  )

  // The designer Preview's inspector: every committed change, read-only.
  const onState = props.onState
  useEffect(() => {
    if (!onState) return
    onState({
      run, frame, step: step?.id ?? null, phase,
      scope: scope ? frameValues(scope) : null,
      issues,
    })
  }, [onState, run, frame, step, scope, phase, issues])

  const backState = model && frame ? canBack(model, frame) : { ok: false }
  const repeat = frame ? repeatProgress(frame) : null
  const remaining = model && frame ? remainingEstimate(model, frame.cursor) : 0

  return {
    phase, assignee: assigneeRef.current, error, host, run, frame, frameIndex, model, base, fi, step, scope, screen, busy, issues,
    startInputs, backState, breadcrumb, paused, finished, refreshKey, ui, repeat, remaining, path, modals,
    next, back, cancel, pause, resume, restart, handBackFailed, jumpTo, change, setUi, submitStart, select, scan, enqueueScan,
    lastScan, undoScan, confirmPassword, setCells, answerCall, print, send, runTool, toolVisible, pickRow, addGridLine, deleteGridLine, retry,
    redirectOpen, redirectPoll, pollJob, linesOf,
  }
}

/** The object a tool reaches, for the grant check (hidden when refused). */
function toolTarget(tool: WizardTool): { type: string; name: string; right: string } | null {
  if (tool.kind === 'open' && tool.open?.obj_name) {
    return { type: tool.open.obj_type || 'T', name: tool.open.obj_name, right: tool.open.obj_type && tool.open.obj_type !== 'T' ? 'view' : 'read' }
  }
  if (tool.kind === 'print' && tool.report?.obj_name) return { type: 'R', name: tool.report.obj_name, right: 'print' }
  if (tool.kind === 'call' && tool.call?.wizard) return { type: 'U', name: tool.call.wizard, right: 'run' }
  return null
}

